import { useEffect, useMemo, useRef, useState } from "react";
import { Typography } from "@remo-co/ui-core/src/components/Typography";
import { LinearProgress } from "@remo-co/ui-core/src/components/LinearProgress";
import { useI18n } from "i18n";
import classNames from "classnames";
import { addInfoNotification } from "modules/notification/redux/notificationSlice";
import { deltaToDuration } from "helpers/time/operations";
import { durationTimestamp } from "helpers/time/format";
import { useTicker } from "modules/dateTime";
import { useAppDispatch } from "store/hooks";
import { calculateSchedule } from "../../TimerHelpers";
import { useStyles } from "./styles";

type TimerStates = "pending" | "active" | "idle" | "finished" | "stale";

export interface ITimerPanelProps {
  startedAt: number;
  breakDuration: number;
  roundDuration: number;
  rounds: number;
  isEventManager: boolean;
  onStart?: () => void;
  onEnd?: () => void;
}

export const TimerPanel = ({
  startedAt,
  breakDuration,
  roundDuration,
  rounds,
  isEventManager,
  onStart,
  onEnd,
}: ITimerPanelProps) => {
  const { t } = useI18n(["common", "event"]);
  const dispatch = useAppDispatch();
  const styles = useStyles();
  const prevTimerState = useRef<TimerStates>("pending");

  const schedule = useMemo(
    () => calculateSchedule(startedAt, breakDuration, roundDuration, rounds),
    [startedAt, breakDuration, roundDuration, rounds],
  );

  const [remainingRounds, setRemainingRounds] = useState(rounds);

  const now = useTicker(!!schedule.length, 200);

  const currentRound = useMemo(() => {
    let round = -1;

    // eslint-disable-next-line no-plusplus
    for (let i = 0; i < schedule.length; i++) {
      if (schedule[i].startTime <= now && schedule[i].endTime >= now) {
        round = i;
        break;
      }
    }

    return round;
  }, [now, schedule]);

  useEffect(() => {
    if (startedAt < 0) {
      setRemainingRounds(rounds - 1);

      return;
    }

    if (currentRound >= 0) {
      setRemainingRounds(rounds - currentRound - 1);
    }
  }, [currentRound, schedule, startedAt, rounds]);

  const timerState = useMemo<TimerStates>(() => {
    if (schedule.length === 0) {
      return "pending";
    }
    const roundsUpcoming = schedule[0].startTime >= now;
    const roundsFinished = schedule[schedule.length - 1].endTime <= now;
    const inRound = currentRound >= 0;

    if (roundsUpcoming) {
      return "pending";
    }

    if (roundsFinished) {
      return "finished";
    }

    if (!inRound) {
      return "idle";
    }

    return "active";
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentRound, schedule]);

  useEffect(() => {
    switch (timerState) {
      case "active": {
        if (prevTimerState.current === "pending") {
          dispatch(
            addInfoNotification({
              message: t("event:timer.started", { roundDuration, rounds }),
              position: "tr",
            }),
          );
          onStart?.();
        }
        break;
      }
      case "idle": {
        if (prevTimerState.current === "pending") {
          onStart?.();
        }
        dispatch(
          addInfoNotification({
            message: t("event:timer.next.round", {
              key: breakDuration * 60,
            }),
            position: "tr",
          }),
        );
        break;
      }
      case "finished": {
        if (prevTimerState.current === "active") {
          dispatch(
            addInfoNotification({
              message: t("event:countdown.timer.finished"),
              position: "tr",
            }),
          );
        }
        onEnd?.();
        break;
      }
      default: {
        break;
      }
    }

    prevTimerState.current = timerState;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timerState]);

  const remainingTime = useMemo(() => {
    if (currentRound >= 0 && schedule.length > 0) {
      return schedule[currentRound].endTime - now;
    }

    return 0;
  }, [now, schedule, currentRound]);

  const percentRemaining = useMemo(() => {
    if (currentRound === -1 || !schedule.length) {
      return 100;
    }

    const { startTime, endTime } = schedule[currentRound];
    const percent = (remainingTime / (endTime - startTime)) * 100;

    return percent;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentRound, remainingTime]);

  return (
    <>
      <LinearProgress
        variant="determinate"
        color="primary"
        classes={{
          root: classNames(styles.progress, {
            [styles.progressStop]:
              timerState === "finished" || timerState === "pending",
            [styles.progressLow]: percentRemaining < 20,
          }),
          bar: classNames(styles.bar, {
            [styles.barStop]:
              timerState === "finished" || timerState === "pending",
            [styles.barLow]: percentRemaining < 20,
          }),
        }}
        value={percentRemaining}
      />
      <div className={styles.remainingTimeWrapper}>
        {isEventManager && (
          <div className={styles.roundsCount}>
            <Typography color="inherit" data-testid="timer-panel-rounds-count">
              {t("event:remaining.rounds", {
                key: remainingRounds,
              })}
            </Typography>
          </div>
        )}
        <Typography color="inherit" data-testid="timer-panel-remaining-time">
          {t("event:remaining.time", {
            key: durationTimestamp(deltaToDuration(remainingTime)),
          })}
        </Typography>
      </div>
    </>
  );
};
